import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  Clock,
  Target,
  BookOpen,
  DollarSign,
  Users,
  Lightbulb,
  Calendar,
  PlayCircle,
  Lock,
  Rocket,
  Brain,
  Award,
  TrendingUp
} from 'lucide-react';
import { paymentAPI, careerAPI } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import InlineSpinner from '../components/InlineSpinner';

const RefinementQuestionsPage = ({ onRoadmapGenerated }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedCareer, setSelectedCareer] = useState(null);

  // Detect if user came from resume upload
  const fromResume = location.state?.fromResume;

  useEffect(() => {
    // Get selected career from navigation state or localStorage
    const career = location.state?.selectedCareer || JSON.parse(localStorage.getItem('selectedCareer') || 'null');
    if (!career) {
      toast.error('No career selected. Redirecting to insights page.');
      navigate('/insights');
      return;
    }
    setSelectedCareer(career);
  }, [location.state, navigate]);

  const questions = [
    {
      id: 'timeline',
      title: 'Career Transition Timeline',
      subtitle: 'When do you want to make this career transition?',
      icon: Calendar,
      type: 'single',
      options: [
        {
          value: 'asap',
          label: 'ASAP (Within 3 months)',
          description: 'Intensive learning schedule, focus on essential skills',
          intensity: 'High Intensity'
        },
        {
          value: '6months',
          label: '3-6 months',
          description: 'Balanced approach with steady progress',
          intensity: 'Medium Intensity'
        },
        {
          value: '1year',
          label: '6-12 months',
          description: 'Comprehensive learning with skill depth',
          intensity: 'Moderate Pace'
        },
        {
          value: '2years',
          label: '1-2 years',
          description: 'Thorough preparation with multiple specializations',
          intensity: 'Relaxed Pace'
        }
      ]
    },
    {
      id: 'commitment',
      title: 'Learning Commitment',
      subtitle: 'How many hours per week can you dedicate to learning?',
      icon: Clock,
      type: 'single',
      options: [
        {
          value: 'part-time',
          label: '5-10 hours/week',
          description: 'Perfect for working professionals',
          schedule: 'Evenings & Weekends'
        },
        {
          value: 'dedicated',
          label: '15-25 hours/week',
          description: 'Serious commitment to career change',
          schedule: 'Daily Study Sessions'
        },
        {
          value: 'intensive',
          label: '30-40 hours/week',
          description: 'Full-time learning approach',
          schedule: 'Immersive Experience'
        },
        {
          value: 'bootcamp',
          label: '40+ hours/week',
          description: 'Bootcamp-style intensive training',
          schedule: 'All-in Approach'
        }
      ]
    },
    {
      id: 'learning_style',
      title: 'Learning Preferences',
      subtitle: 'What learning methods work best for you?',
      icon: BookOpen,
      type: 'multiple',
      options: [
        {
          value: 'hands-on',
          label: 'Hands-on Projects',
          description: 'Learn by building real applications',
          icon: PlayCircle
        },
        {
          value: 'structured',
          label: 'Structured Courses',
          description: 'Step-by-step guided learning paths',
          icon: Target
        },
        {
          value: 'reading',
          label: 'Self-directed Reading',
          description: 'Books, articles, and documentation',
          icon: BookOpen
        },
        {
          value: 'mentorship',
          label: 'Mentorship & Coaching',
          description: '1-on-1 guidance from industry experts',
          icon: Users
        },
        {
          value: 'community',
          label: 'Community Learning',
          description: 'Study groups and peer collaboration',
          icon: Users
        },
        {
          value: 'video',
          label: 'Video Tutorials',
          description: 'Visual learning through video content',
          icon: PlayCircle
        }
      ]
    },
    {
      id: 'budget',
      title: 'Learning Investment',
      subtitle: 'What is your budget for learning resources and tools?',
      icon: DollarSign,
      type: 'single',
      options: [
        {
          value: 'free',
          label: 'Free Resources Only',
          description: 'Open-source materials and free courses',
          budget: '$0'
        },
        {
          value: 'low',
          label: 'Budget-Friendly',
          description: 'Affordable courses and essential tools',
          budget: '$50 - $200'
        },
        {
          value: 'medium',
          label: 'Moderate Investment',
          description: 'Premium courses and professional tools',
          budget: '$200 - $600'
        },
        {
          value: 'high',
          label: 'Premium Learning',
          description: 'Top-tier courses, bootcamps, and mentoring',
          budget: '$600+'
        }
      ]
    },
    {
      id: 'focus_areas',
      title: 'Specialization Focus',
      subtitle: 'Which aspects of this career interest you most?',
      icon: Target,
      type: 'multiple',
      options: selectedCareer?.title === 'Data Analyst' ? [
        { value: 'visualization', label: 'Data Visualization', description: 'Creating compelling charts and dashboards' },
        { value: 'statistics', label: 'Statistical Analysis', description: 'Advanced statistical methods and modeling' },
        { value: 'tools', label: 'Technical Tools', description: 'Mastering Python, R, SQL, and BI tools' },
        { value: 'business', label: 'Business Intelligence', description: 'Translating data into business insights' },
        { value: 'machine-learning', label: 'Machine Learning', description: 'Predictive analytics and ML algorithms' }
      ] : [
        { value: 'technical', label: 'Technical Skills', description: 'Core technical competencies' },
        { value: 'leadership', label: 'Leadership', description: 'Team management and leadership skills' },
        { value: 'strategy', label: 'Strategic Thinking', description: 'Business strategy and planning' },
        { value: 'communication', label: 'Communication', description: 'Presentation and interpersonal skills' },
        { value: 'innovation', label: 'Innovation', description: 'Creative problem-solving and innovation' }
      ]
    }
  ];

  const handleAnswer = (questionId, value) => {
    const question = questions.find(q => q.id === questionId);

    if (question.type === 'multiple') {
      setAnswers(prev => ({
        ...prev,
        [questionId]: prev[questionId]
          ? (prev[questionId].includes(value)
            ? prev[questionId].filter(v => v !== value)
            : [...prev[questionId], value])
          : [value]
      }));
    } else {
      setAnswers(prev => ({
        ...prev,
        [questionId]: value
      }));
    }
  };

  const isStepComplete = (stepIndex) => {
    const question = questions[stepIndex];
    const answer = answers[question.id];

    if (question.type === 'multiple') {
      return answer && answer.length > 0;
    }
    return !!answer;
  };

  const allStepsComplete = questions.every((_, index) => isStepComplete(index));

  const nextStep = () => {
    if (currentStep < questions.length - 1 && isStepComplete(currentStep)) {
      setCurrentStep(currentStep + 1);
      // Smooth scroll to progress bar area
      setTimeout(() => {
        const progressBar = document.querySelector('.progress-section');
        if (progressBar) {
          progressBar.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      // Smooth scroll to progress bar area
      setTimeout(() => {
        const progressBar = document.querySelector('.progress-section');
        if (progressBar) {
          progressBar.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const handlePayment = async () => {
    if (!allStepsComplete) {
      toast.error('Please answer all questions before proceeding.');
      return;
    }

    setIsProcessing(true);
    try {
      const profileData = JSON.parse(localStorage.getItem('userProfile') || '{}');

      // Store refinement answers
      localStorage.setItem('refinementAnswers', JSON.stringify(answers));
      localStorage.setItem('selectedCareer', JSON.stringify(selectedCareer));

      // Process payment
      const paymentResponse = await paymentAPI.createTestPayment(
        profileData?.name || 'Test User',
        'test@example.com'
      );

      toast.success('Payment successful! Generating your personalized roadmap...');

      // Generate detailed roadmap
      const roadmapData = await careerAPI.generateRoadmap(
        profileData,
        selectedCareer.title,
        paymentResponse.order_id,
        answers // Pass refinement answers to customize the roadmap
      );

      // Store roadmap data in localStorage for immediate access
      localStorage.setItem('roadmapData', JSON.stringify(roadmapData));

      // Store roadmap ID for progress tracking
      if (roadmapData.roadmap_id) {
        localStorage.setItem('selectedRoadmapId', roadmapData.roadmap_id);
      } else {
        localStorage.removeItem('selectedRoadmapId');
      }

      // The roadmap is automatically saved to Firestore by the backend
      // when generateRoadmap is called with authentication

      if (onRoadmapGenerated) {
        onRoadmapGenerated(roadmapData);
      }

      toast.success('Roadmap generated and saved successfully!');
      navigate('/roadmap');

    } catch (error) {
      console.error('Payment or roadmap generation failed:', error);
      toast.error(error.message || 'An error occurred. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (!selectedCareer) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const currentQuestion = questions[currentStep];
  const progress = ((currentStep + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <div className="max-w-4xl mx-auto px-4 py-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center space-x-2 bg-emerald-900/50 text-emerald-300 rounded-full px-6 py-3 mb-6">
            <Brain className="w-5 h-5" />
            <span className="text-sm font-medium">Personalizing Your Roadmap</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-2">
            Let's Create Your Perfect Learning Path
          </h1>
          <p className="text-slate-400 text-lg">
            For: <span className="text-emerald-400 font-semibold">{selectedCareer.title}</span>
          </p>
        </div>

        {/* Resume User Welcome Banner */}
        {fromResume && (
          <div className="bg-gradient-to-r from-blue-900/80 to-indigo-900/80 border border-blue-700/50 rounded-xl p-6 mb-8">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircle className="w-8 h-8 text-blue-400" />
              </div>
              <div className="ml-4">
                <h3 className="font-bold text-xl text-white mb-2">
                  📄 Great! Your Resume Analysis is Complete
                </h3>
                <p className="text-blue-200">
                  Now let's personalize your <strong>{selectedCareer.title}</strong> roadmap based on your timeline,
                  learning style, and goals. This will help us create the most effective learning path for you.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Progress Bar */}
        <div className="progress-section mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-slate-400">
              Step {currentStep + 1} of {questions.length}
            </span>
            <span className="text-sm text-slate-400">
              {Math.round(progress)}% Complete
            </span>
          </div>
          <div className="w-full bg-slate-700 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-emerald-500 to-teal-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Question Card */}
        <div className="question-card bg-slate-800/50 border border-slate-700 rounded-2xl p-6 mb-6">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center mr-4">
              <currentQuestion.icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">{currentQuestion.title}</h2>
              <p className="text-slate-400">{currentQuestion.subtitle}</p>
            </div>
          </div>

          {/* Options */}
          <div className="space-y-4">
            {currentQuestion.options.map((option) => {
              const isSelected = currentQuestion.type === 'multiple'
                ? answers[currentQuestion.id]?.includes(option.value)
                : answers[currentQuestion.id] === option.value;

              return (
                <button
                  key={option.value}
                  onClick={() => handleAnswer(currentQuestion.id, option.value)}
                  className={`w-full p-4 rounded-xl text-left transition-all border-2 ${isSelected
                    ? 'bg-emerald-600/20 border-emerald-500 text-white'
                    : 'bg-slate-700/50 border-slate-600 hover:border-slate-500 text-slate-300'
                    }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        {option.icon && <option.icon className="w-5 h-5 mr-2 text-emerald-400" />}
                        <h3 className="font-semibold text-lg">{option.label}</h3>
                      </div>
                      <p className="text-slate-400 mb-2">{option.description}</p>
                      {option.intensity && (
                        <span className="inline-block bg-blue-900/50 text-blue-300 text-xs px-2 py-1 rounded">
                          {option.intensity}
                        </span>
                      )}
                      {option.schedule && (
                        <span className="inline-block bg-purple-900/50 text-purple-300 text-xs px-2 py-1 rounded ml-2">
                          {option.schedule}
                        </span>
                      )}
                      {option.budget && (
                        <span className="inline-block bg-green-900/50 text-green-300 text-xs px-2 py-1 rounded ml-2">
                          {option.budget}
                        </span>
                      )}
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-emerald-500 bg-emerald-500' : 'border-slate-500'
                      }`}>
                      {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {currentQuestion.type === 'multiple' && (
            <p className="text-slate-400 text-sm mt-4">
              💡 You can select multiple options
            </p>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <button
            onClick={prevStep}
            disabled={currentStep === 0}
            className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all ${currentStep === 0
              ? 'text-slate-500 cursor-not-allowed'
              : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
              }`}
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Previous</span>
          </button>

          {currentStep < questions.length - 1 ? (
            <button
              onClick={nextStep}
              disabled={!isStepComplete(currentStep)}
              className={`flex items-center space-x-2 px-8 py-3 rounded-xl font-medium transition-all ${isStepComplete(currentStep)
                ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-500 hover:to-teal-500 transform hover:scale-105'
                : 'bg-slate-700 text-slate-400 cursor-not-allowed'
                }`}
            >
              <span>Continue</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={handlePayment}
              disabled={!allStepsComplete || isProcessing}
              className={`flex items-center space-x-3 px-8 py-4 rounded-xl font-bold text-lg transition-all ${allStepsComplete && !isProcessing
                ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-500 hover:to-teal-500 transform hover:scale-105 shadow-lg'
                : 'bg-slate-700 text-slate-400 cursor-not-allowed'
                }`}
            >
              {isProcessing ? (
                <>
                  <InlineSpinner size="sm" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <Lock className="w-6 h-6" />
                  <span>Upgrade to Starter Plan</span>
                  <span className="bg-white/20 px-3 py-1 rounded-full text-sm">$5/mo</span>
                </>
              )}
            </button>
          )}
        </div>

        {/* Summary Preview */}
        {Object.keys(answers).length > 0 && (
          <div className="mt-8 bg-slate-800/30 border border-slate-700 rounded-xl p-6">
            <h3 className="font-bold text-lg text-white mb-4 flex items-center">
              <Lightbulb className="w-5 h-5 text-yellow-400 mr-2" />
              Your Preferences So Far
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              {Object.entries(answers).map(([questionId, answer]) => {
                const question = questions.find(q => q.id === questionId);
                if (!question) return null;

                return (
                  <div key={questionId} className="bg-slate-700/50 p-3 rounded-lg">
                    <div className="font-medium text-emerald-300 mb-1">{question.title}</div>
                    <div className="text-slate-300">
                      {Array.isArray(answer) ? answer.join(', ') :
                        question.options.find(opt => opt.value === answer)?.label || answer}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RefinementQuestionsPage; 